import { userManager } from 'auth/oidc';

export class HttpService {
  /**
   * @example
   * // Register an error event listener
   * const errorCallback = (event: ErrorEvent) => {
   *   console.error(`Error occurred: ${event.error.message}`);
   * };
   * onError.addEventListener('error', errorCallback);
   */
  static onError = new EventTarget();


  static async getAuthToken(): Promise<string | null> {
    try {
      const user = await userManager.getUser();
      if (user && user.access_token) {
        return user.access_token;
      } else {
        console.log('User not logged in');
        return null;
      }
    } catch (error) {
      console.error('Error getting the access token:', error);
      return null;
    }
  }

  static async get(url: string) {
    const accessToken = await HttpService.getAuthToken();
    if (accessToken) {
      try {
        let options: any = {
          method: 'GET',
          headers: {
            'Accept': 'application/json',
            'Content-Type': 'application/json',
            'X-Forwarded-Access-Token': `Bearer ${accessToken}`
          },
          mode: 'cors',
        };

        const response = await fetch(url, options);

        if (!response.ok) {
          const errorText = response.statusText;
          throw new Error(`HTTP Error: ${response.status} - ${errorText}`);
        }

        return await response.json();
      } catch (error) {
        const errorMessage = `Error during fetch "${url}": ${error}`;
        console.error('[HttpService]: Error:', errorMessage);
        this.onError.dispatchEvent(new ErrorEvent('error', { error: errorMessage }))
      }
    } else {
      console.error('[HttpService]: No accessToken');
      throw new Error('Access token is not available. User might not be logged in.');
    }
  }

  static async post(url: string, body?: any) {
    const accessToken = await HttpService.getAuthToken();
    if (accessToken) {
      try {
        let options: any = {
          method: 'POST',
          headers: {
            'Accept': 'application/json',
            'Content-Type': 'application/json',
            'X-Forwarded-Access-Token': `Bearer ${accessToken}`
          },
          mode: 'cors',
          body: JSON.stringify(body),
        };

        const response = await fetch(url, options);

        if (!response.ok) {
          const errorText = response.statusText;
          throw new Error(`HTTP Error: ${response.status} - ${errorText}`);
        }

        return await response.json();
      } catch (error) {
        const errorMessage = `Error during fetch "${url}": ${error}`;
        console.error('[HttpService]: Error:', errorMessage);
        this.onError.dispatchEvent(new ErrorEvent('error', { error: errorMessage }))
      }
    } else {
      console.error('[HttpService]: No accessToken');
      throw new Error('Access token is not available. User might not be logged in.');
    }
  }

  static async put(url: string, body?: any) {
    const accessToken = await HttpService.getAuthToken();
    if (accessToken) {
      try {
        let options: any = {
          method: 'PUT',
          headers: {
            'Accept': 'application/json',
            'Content-Type': 'application/json',
            'X-Forwarded-Access-Token': `Bearer ${accessToken}`
          },
          mode: 'cors',
          body: JSON.stringify(body),
        };

        const response = await fetch(url, options);

        if (!response.ok) {
          const errorText = response.statusText;
          throw new Error(`HTTP Error: ${response.status} - ${errorText}`);
        }

        return await response.json();
      } catch (error) {
        const errorMessage = `Error during fetch "${url}": ${error}`;
        console.error('[HttpService]: Error:', errorMessage);
        this.onError.dispatchEvent(new ErrorEvent('error', { error: errorMessage }))
      }
    } else {
      console.error('[HttpService]: No accessToken');
      throw new Error('Access token is not available. User might not be logged in.');
    }
  }

  static async delete(url: string) {
    const accessToken = await HttpService.getAuthToken();
    if (accessToken) {
      try {
        let options: any = {
          method: 'DELETE',
          headers: {
            'Accept': 'application/json',
            'Content-Type': 'application/json',
            'X-Forwarded-Access-Token': `Bearer ${accessToken}`
          },
          mode: 'cors',
        };

        const response = await fetch(url, options);

        if (!response.ok) {
          const errorText = response.statusText;
          throw new Error(`HTTP Error: ${response.status} - ${errorText}`);
        }

        return await response.json();
      } catch (error) {
        const errorMessage = `Error during fetch "${url}": ${error}`;
        console.error('[HttpService]: Error:', errorMessage);
        this.onError.dispatchEvent(new ErrorEvent('error', { error: errorMessage }))
      }
    } else {
      console.error('[HttpService]: No accessToken');
      throw new Error('Access token is not available. User might not be logged in.');
    }
  }
}
